import {
  Grid,
  Typography,
  CardContent,
  Card,
  Box,
  Divider,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { TextField, useRecordContext, TextInput, ImageInput, ImageField, required, SelectInput } from "react-admin";
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import FormField from "../../../components/Common/FormField";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { ColorInput } from "react-admin-color-picker";
import { useWatch } from "react-hook-form";
import { IfCanAccess } from "@react-admin/ra-rbac";
const LogoUpload = ({
  field,
}: {
  field: string;
}) => {
  const project = useRecordContext();
  const { t }: { t: any } = useTranslation();
  const physicalPayment = project?.parameters?.physicalPayment || {};
  const src = physicalPayment[field];
  return (
    <Grid container spacing={2} alignItems="flex-start">
      <Grid item xs={12} sm={4} md={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            height: 96,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            backgroundColor: src && field === "logo" ? physicalPayment.backgroundColor : "transparent",
          }}
        >
          {src ? (
            <img src={src} alt={t("Logo preview")} style={{ maxWidth: "80%", maxHeight: "80%" }} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              {t("No logo")}
            </Typography>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={8} md={9}>
        <ImageInput
          label={false}
          source={field}
          accept="image/*,.png,.jpg,.jpeg"
          maxSize={2000000}
          validate={required()}
        >
          <ImageField
            source="src"
            title="title"
            sx={{
              backgroundColor: physicalPayment.backgroundColor,
            }}
          />
        </ImageInput>
      </Grid>
    </Grid>
  );
};

// A color input with its label above and a swatch of the current value beside it.
const SwatchColorInput = ({ source, label, isRequired }: { source: string; label: string; isRequired?: boolean }) => {
  const value = useWatch({ name: source });
  return (
    <Box display="flex" alignItems="flex-end" gap={1.5} sx={{ "& > div": { flex: 1 }, "& .MuiFormControl-root": { width: "100%" } }}>
      <ColorInput source={source} label={label} isRequired={isRequired} variant="filled" />
      <Box
        sx={{
          flex: "0 0 auto !important",
          width: 44,
          height: 44,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          backgroundColor: value || "transparent",
        }}
      />
    </Box>
  );
};

const SectionTitle = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <Box pb={2}>
    <Typography variant="h6">{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {subtitle}
    </Typography>
  </Box>
);
function ParametersCryoptoPaymentTabEdit() {
  const { t }: { t: any } = useTranslation();
  const project = useRecordContext();
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <SectionTitle title={t("Interface configuration")} subtitle={t("Name, contact details, logos and colors shown to customers")} />
        <AnimatedBlock>
          <Card sx={{ mb: 3 }}>
            <CardContent
              sx={{
                p: 4,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Name")}>
                    <TextInput
                      source="parameters.physicalPayment.name"
                      fullWidth
                      label={t("Name")}
                      validate={required()}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={12}>
                  <FormField label={t("Description")}>
                    <TextInput
                      source="parameters.physicalPayment.description"
                      fullWidth
                      label={t("Description")}
                      validate={required()}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={12}>
                  <FormField label={t("Address")}>
                    <TextInput
                      source="parameters.physicalPayment.address"
                      fullWidth
                      label={t("Address")}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Phone")}>
                    <TextInput
                      source="parameters.physicalPayment.phone"
                      fullWidth
                      label={t("Phone")}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Email")}>
                    <TextInput
                      source="parameters.physicalPayment.email"
                      fullWidth
                      label={t("Email")}
                      validate={required()}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={12}>
                  <FormField label={t("Logo")}>
                    <LogoUpload field="logo" />
                  </FormField>
                </Grid>
                <Grid item xs={12}>
                  <FormField label={t("Inverted logo")}>
                    <LogoUpload field="logoInverse" />
                  </FormField>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Background color")}>
                    <SwatchColorInput source="parameters.physicalPayment.backgroundColor" label={t("Background color")} isRequired />
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Font color")}>
                    <SwatchColorInput source="parameters.physicalPayment.fontColor" label={t("Font color")} />
                  </FormField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </AnimatedBlock>
        <SectionTitle title={t("Crypto payment configuration")} subtitle={t("Blockchain, conversion and API access of this entity")} />
        <AnimatedBlock>
          <Card sx={{ mb: 3 }}>
            <CardContent
              sx={{
                p: 4,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Blockchain")}>
                    <SelectInput fullWidth
                      source="parameters.blockchain"
                      label={t("Blockchain")}
                      choices={[{ id: "ethereum", name: "Ethereum" }]}
                      />
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Network")}>
                    <SelectInput fullWidth
                      source="parameters.network"
                      label={t("Network")}
                      choices={[
                          { id: "mainnet", name: "mainnet" },
                          { id: "sepolia", name: "sepolia" },
                        ]}
                      />
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField label={t("Auto conversion")}>
                    <SelectInput fullWidth
                      source="autoConvert"
                      label={t("Auto conversion")}
                      choices={[
                        { id: true, name: "Enabled" },
                        { id: false, name: "Disabled" },
                      ]}
                      defaultValue={true}
                    />
                  </FormField>
                </Grid>
                <Grid item xs={12}>
                  <FormField label={t("Webhook URL")}>
                    <TextInput source="parameters.webhookUrl" label={t("Webhook URL")} fullWidth />
                  </FormField>
                </Grid>
                <Grid item xs={12}>
                  <FormField label={t("API key")}>
                    <Box display="flex" alignItems="center">
                      {project?.apiKey ? (
                        <Typography>
                          {project.apiKey.substring(0, 10)}......
                          {project.apiKey.substring(
                            project.apiKey.length - 10
                          )}
                        </Typography>
                      ) : (
                        <TextField source="apiKey" />
                      )}
                      <Button
                        sx={{
                          marginLeft: 3,
                        }}
                        onClick={() => {
                          const apiKey = project?.apiKey || "";
                          navigator.clipboard.writeText(apiKey);
                        }}
                        startIcon={<ContentCopyOutlinedIcon />}
                      >
                        {t("Copy")}
                      </Button>
                    </Box>
                  </FormField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </AnimatedBlock>
        <IfCanAccess resource="projects.userIdCNHS" action="create">
        <SectionTitle title={t("Coinhouse configuration")} subtitle={t("The Coinhouse user this entity is linked to")} />
        <AnimatedBlock>
          <Card sx={{ mb: 3 }}>
            <CardContent
              sx={{
                p: 4,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormField label={t("Coinhouse user UUID")}>
                    <TextInput
                      source="userIdCNHS"
                      sx={{ "& input": { fontFamily: "monospace" } }}
                      label={t("Coinhouse user UUID")}
                      fullWidth
                    />
                  </FormField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </AnimatedBlock>
        </IfCanAccess>
      </Grid>
    </Grid>
  );
}

export default ParametersCryoptoPaymentTabEdit;
